import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { Day } from '../../models/business/PolicyShift'
import { daySchema } from '../../models/schema'
import {
  createDay as createDayAPI,
  deleteDay as deleteDayAPI,
  fetchDayById,
  fetchDays,
  updateDay as updateDayAPI,
} from '../../services/business/DayService'
import { AppDispatch, RootState } from './../store'

const initialState: SBState<Day> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'day',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getDaysSuccess,
  setQuery: setDayQuery,
  reset: resetDayState,
  resetQueryAndIds: resetDayQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Day }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectDays = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectAllDays = () =>
  createSelector([selectRawItems], (items) =>
    Object.entries(items).map((i) => i[1])
  )

export const selectSelectedDay = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectDayById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectDaysByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedDay =
  (day: Day | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Day>(day)))

export const getDays = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(fetchDays(params), [daySchema], slice.actions)

export const createDay = (day: Day) =>
  SBAPI.CreateDispatched(createDayAPI(day), daySchema, slice.actions)

export const getDayWithId = (id: number) =>
  SBAPI.FetchDispatched<Day>(fetchDayById(id), daySchema, slice.actions)

export const updateDay = (day: Day) =>
  SBAPI.UpdateDispatched<Day>(updateDayAPI(day), day, slice.actions)

export const deleteDay = (day: Day) =>
  SBAPI.DestroyDispatched<Day>(deleteDayAPI(day), day, slice.actions)
